import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Container, Button, Image, ProgressBar} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {RootState} from 'state';
import Spinner from 'components/Spinner';
//@ts-ignore
import paymentSuccess from '../assets/images/payment-success.png';
//@ts-ignore
import paymentFailed from '../assets/images/failed.png';
import './PaymentResult.css';

interface PaymentResultProps {
  type?: 'success' | 'failure';
}

interface PaymentDetails {
  amount?: string;
  currency?: string;
  vat?: string;
}

const REDIRECT_DELAY = 5; // seconds

const PaymentResult: React.FC<PaymentResultProps> = ({type}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  const urlParams = new URLSearchParams(location.search);
  const orderId = location.state?.orderId || urlParams.get('orderId');
  const paymentDetails: PaymentDetails = {
    amount: urlParams.get('amount') || '',
    currency: urlParams.get('currency') || '',
    vat: urlParams.get('vat') || '',
  };

  const {user, token, isLogged} = useSelector(
    (state: RootState) => state.login,
  );

  const goToVideoCall = () => {
    setIsLoading(true);
    const payment_id = urlParams.get('payment_id') || '';
    navigate('/video-call', {
      state: {
        isLoggedIn: isLogged,
        user: user,
        logInToken: token,
        isFollowUp: false,
        payment_id: payment_id,
      },
    });
  };

  const handleRetry = () => {
    setIsLoading(true);
    navigate('/questions');
  };

  useEffect(() => {
    if (!orderId) {
      navigate('/');
      return;
    }

    // Start countdown for auto-redirect
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          type === 'success' ? goToVideoCall() : handleRetry();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [orderId, navigate, location, type]);

  // Add GTM tracking for successful payments
  useEffect(() => {
    if (type === 'success' && paymentDetails.amount) {
      const dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: 'paymentSuccess',
        result: {
          value: paymentDetails.amount,
          currency: paymentDetails.currency,
          order_id: orderId,
          vat: paymentDetails.vat,
        },
      });
    }
  }, [type, paymentDetails, orderId]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Container className="text-center mt-5 payment-result">
      <div className="result-card p-4 rounded shadow-sm">
        <Image
          src={type === 'success' ? paymentSuccess : paymentFailed}
          alt={type === 'success' ? 'Success' : 'Failed'}
          className="mb-4 result-image"
          width="120"
        />
        <h2 className="mb-3">
          {type === 'success'
            ? t('pages.payment.success')
            : t('pages.payment.failed')}
        </h2>
        <p className="text-muted mb-4">
          {type === 'success'
            ? t('pages.payment.successMessage')
            : t('pages.payment.failedMessage')}
        </p>
        {orderId && (
          <p className="order-id mb-4">
            {t('pages.payment.orderId')}:{' '}
            <span className="fw-bold">{orderId}</span>
          </p>
        )}
        <div className="redirect-info mb-4">
          <p className="text-muted mb-2">
            {type === 'success'
              ? t('pages.payment.redirectingToConsultation')
              : t('pages.payment.redirectingToQuestions')}
          </p>
          <ProgressBar
            now={(countdown / REDIRECT_DELAY) * 100}
            className="mb-3"
            variant={type === 'success' ? 'success' : 'warning'}
          />
          <small className="text-muted">
            {t('pages.payment.redirectIn', {seconds: countdown})}
          </small>
        </div>
        <Button
          onClick={type === 'success' ? goToVideoCall : handleRetry}
          className="btnPrimary w-100"
        >
          {type === 'success'
            ? t('pages.payment.startNow')
            : t('pages.payment.tryAgain')}
        </Button>
      </div>
    </Container>
  );
};

export default PaymentResult;
